/**
 * The question being answered, shared by the Ask page and its button (one at a time: a new
 * question stops the one before). The button can start it on any page and the page show it
 * live when opened ("Open in full view"); when it ends, the person's threads are read again.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AskRefused, streamAsk } from './askApi'
import type { AskStatus } from './askApi'
import { failRun, reduceRun, startRun } from './askRun'
import type { AskRun } from './askRun'

export interface AskQuestion {
    question: string
    page: string | null
    state: Record<string, string>
    threadId: string | null
}

interface AskContextValue {
    status: AskStatus | null
    run: AskRun | null
    /** Asks it; returns the run's key (AskRun.key). */
    ask: (question: AskQuestion) => number
    /** Forget the run (a new question starts a new thread). */
    clear: () => void
    /** A page's title by its id, for "Asked on …" (null for a page this app doesn't show). */
    pageTitle: (id: string | null) => string | null
}

const AskContext = createContext<AskContextValue | null>(null)

export function useAsk(): AskContextValue | null {
    return useContext(AskContext)
}

export function AskProvider({ status, pageTitle, children }: { status: AskStatus | null; pageTitle: (id: string | null) => string | null; children: ReactNode }) {
    const { i18n } = useTranslation()
    const client = useQueryClient()
    const [run, setRun] = useState<AskRun | null>(null)
    const controller = useRef<AbortController | null>(null)

    useEffect(() => () => controller.current?.abort(), [])

    const ask = useCallback(
        ({ question, page, state, threadId }: AskQuestion) => {
            controller.current?.abort()
            const abort = new AbortController()
            controller.current = abort
            const fresh = startRun(question, page, state, threadId)
            setRun(fresh)
            const mine = (update: (r: AskRun) => AskRun) => setRun((current) => (current && current.key === fresh.key ? update(current) : current))
            streamAsk(
                { question, lang: i18n.language, page, state, thread_id: threadId },
                { signal: abort.signal, onEvent: (event) => mine((r) => reduceRun(r, event)) },
            )
                .then(() => mine((r) => failRun(r, 'no_answer')))
                .catch((err: unknown) => {
                    if (err instanceof DOMException && err.name === 'AbortError') return
                    mine((r) => failRun(r, err instanceof AskRefused ? err.code : 'failed'))
                })
                .finally(() => {
                    void client.invalidateQueries({ queryKey: ['ask-threads'] })
                    void client.invalidateQueries({ queryKey: ['ask-thread'] })
                })
            return fresh.key
        },
        [client, i18n.language],
    )

    const clear = useCallback(() => {
        controller.current?.abort()
        controller.current = null
        setRun(null)
    }, [])

    const value = useMemo(() => ({ status, run, ask, clear, pageTitle }), [status, run, ask, clear, pageTitle])
    return <AskContext.Provider value={value}>{children}</AskContext.Provider>
}
